import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  Snackbar
} from '@mui/material';
import { ContentCopy, Check } from '@mui/icons-material';
import { User } from 'modules/Interfaces/Company';

interface ResetPasswordDialogProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  onReset: () => Promise<{ password: string } | null>;
  loading: boolean;
}

export const ResetPasswordDialog: React.FC<ResetPasswordDialogProps> = ({
  open,
  onClose,
  user,
  onReset,
  loading
}) => {
  const [newPassword, setNewPassword] = useState<string>('');
  const [showCopied, setShowCopied] = useState<boolean>(false);
  const [hasReset, setHasReset] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setNewPassword('');
      setHasReset(false);
      setShowCopied(false);
      setError('');
    }
  }, [open]);

  const handleReset = async () => {
    try {
      setError('');
      const result = await onReset();
      if (result?.password) {
        setNewPassword(result.password);
        setHasReset(true);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(errorMessage);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(newPassword);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = newPassword;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {!hasReset ? (
            <>
              <Typography sx={{ pt: 1, mb: 2 }}>
                Are you sure you want to reset the password for user "{user?.email}"?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                A new random password will be generated and displayed. The user will need to change it on their next login.
              </Typography>
            </>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Password Reset Successful!
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  New password has been generated for user "{user?.email}".
                </Typography>
              </Alert>
              
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                New Password:
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                p: 2, 
                bgcolor: 'grey.100', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300'
              }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontFamily: 'monospace', 
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    color: 'primary.main',
                    flex: 1
                  }}
                >
                  {newPassword}
                </Typography>
                <IconButton 
                  onClick={copyToClipboard}
                  size="small"
                  color="primary"
                  title="Copy to clipboard"
                >
                  <ContentCopy />
                </IconButton>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Please copy this password and share it securely with the user. The password will not be shown again.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            color="secondary"
            variant="outlined"
          >
            {hasReset ? 'Close' : 'Cancel'}
          </Button>
          {!hasReset && (
            <Button 
              onClick={handleReset} 
              color="warning" 
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showCopied}
        autoHideDuration={2000}
        onClose={() => setShowCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowCopied(false)} 
          severity="success" 
          icon={<Check />}
          sx={{ width: '100%' }}
        >
          Password copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
}; 